<template>
  <v-expansion-panel>
    <v-expansion-panel-header>
      <div class="d-flex align-center">
        <v-chip
          v-if="event.isEnabled"
          small
          color="success"
          text-color="white"
          class="event-setting__state flex-shrink-0 me-4"
        >
          включено
        </v-chip>
        <v-chip
          v-else
          small
          class="event-setting__state flex-shrink-0 me-4"
        >
          выключено
        </v-chip>

        <div class="flex-grow-1">
          <div class="text-subtitle-2">
            {{ event.name }}
          </div>
          <div class="text-caption text--secondary">
            {{ event.description }}
          </div>
          <div class="text-caption text--secondary">
            {{ note }}
          </div>
        </div>

        <span
          v-if="isDirty"
          class="text-caption warning--text text-no-wrap ms-4"
        >
          не сохранено
        </span>
        <span class="text-caption text--secondary text-no-wrap mx-4">
          за 7 дней: {{ event.sentLastWeek }}
        </span>
      </div>
    </v-expansion-panel-header>

    <v-expansion-panel-content>
      <v-form
        ref="form"
        class="multi-col-validation"
      >
        <v-switch
          v-model="form.isEnabled"
          label="Уведомление включено"
          hide-details
          class="mt-0"
        ></v-switch>

        <div
          v-if="event.placeholders.length"
          class="d-flex align-center flex-wrap mt-6"
        >
          <span class="text-caption text--secondary me-2">Подстановки:</span>
          <v-chip
            v-for="name in event.placeholders"
            :key="name"
            small
            label
            class="me-2"
            @click="insert(name)"
          >
            {{ placeholder(name) }}
          </v-chip>
          <span class="text-caption text--secondary">
            нажмите, чтобы вставить в поле, где стоит курсор
          </span>
        </div>

        <v-row class="mt-2">
          <v-col
            v-for="text in form.texts"
            :key="text.language"
            cols="12"
            md="4"
          >
            <div class="d-flex align-center mb-2">
              <span class="text-subtitle-2 me-2">{{ languageName(text.language) }}</span>
              <v-chip
                v-if="isDefaultText(text)"
                x-small
              >
                по умолчанию
              </v-chip>
            </div>

            <v-text-field
              v-model="text.title"
              label="Заголовок"
              counter
              dense
              outlined
              :error-messages="messages(`title.${text.language}`)"
              @focus="remember(text, 'title', $event)"
            ></v-text-field>

            <v-textarea
              v-model="text.body"
              label="Текст"
              rows="3"
              auto-grow
              counter
              dense
              outlined
              :error-messages="messages(`body.${text.language}`)"
              @focus="remember(text, 'body', $event)"
            ></v-textarea>

            <v-btn
              v-if="!isDefaultText(text)"
              small
              text
              @click="restore(text)"
            >
              Вернуть текст по умолчанию
            </v-btn>
          </v-col>
        </v-row>

        <v-row
          v-if="event.parameters.length"
          class="mt-2"
        >
          <v-col
            v-if="has('sendAt')"
            cols="12"
            md="3"
          >
            <v-text-field
              v-model="form.sendAt"
              label="Во сколько поздравлять"
              type="time"
              dense
              outlined
              :rules="[rules.required]"
              :error-messages="messages('sendAt')"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="has('radiusMeters')"
            cols="12"
            md="3"
          >
            <v-text-field
              v-model.number="form.radiusMeters"
              label="Радиус зоны, м"
              type="number"
              min="0"
              dense
              outlined
              :rules="[rules.required, rules.integerValidator]"
              :error-messages="messages('radiusMeters')"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="has('cooldownDays')"
            cols="12"
            md="3"
          >
            <v-text-field
              v-model.number="form.cooldownDays"
              label="Не чаще раза в, дней"
              type="number"
              min="0"
              dense
              outlined
              :rules="[rules.required, rules.integerValidator]"
              :error-messages="messages('cooldownDays')"
            ></v-text-field>
          </v-col>

          <v-col
            v-if="has('link')"
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="form.link"
              label="Куда ведёт нажатие"
              hint="Путь («/promotions») или адрес с https://. Пусто — приложение решает само"
              persistent-hint
              dense
              outlined
              :error-messages="messages('link')"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="d-flex align-center flex-wrap mt-4">
          <v-btn
            color="primary"
            class="me-3"
            :loading="saving === key"
            :disabled="hasNotPermission('Permissions.NotificationSettingsUpdate')"
            @click="save"
          >
            Сохранить
          </v-btn>
          <v-btn
            color="primary"
            outlined
            class="me-3"
            :disabled="isDirty || hasNotPermission('Permissions.NotificationTestSend')"
            @click="$emit('test', event)"
          >
            Проверить
          </v-btn>
          <span
            v-if="isDirty"
            class="text-caption text--secondary"
          >
            Проверка отправляет сохранённый текст — сначала сохраните правки
          </span>
        </div>

        <div class="text-caption text--secondary mt-4">
          Последняя отправка: {{ toDateTimeString(event.lastSentAt) || 'ещё не было' }}<span v-if="event.updatedAt"> · изменено {{ toDateTimeString(event.updatedAt) }}<span v-if="event.updatedBy"> · {{ event.updatedBy }}</span></span>
        </div>
      </v-form>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { rules } from '@/helpers/validation'
import { errorMessages, hasPermission, toDateTimeString } from '@/helpers'
import { eventKey, languageName } from '@/helpers/notifications'

const draftOf = event => ({
  isEnabled: event.isEnabled,
  link: event.link || '',
  sendAt: event.sendAt || '',
  radiusMeters: event.radiusMeters,
  cooldownDays: event.cooldownDays,
  texts: event.texts.map(text => ({ ...text, title: text.title || '', body: text.body || '' })),
})

export default {
  name: 'EventSettingPanel',

  props: {
    event: { type: Object, required: true },
  },
  data: () => ({
    rules,
    languageName,
    toDateTimeString,
    form: null,

    saved: null,

    cursor: null,
  }),
  computed: {
    ...mapState('notificationSettings', ['saving']),
    ...mapGetters('notificationSettings', ['errorsOf']),
    key() {
      return eventKey(this.event.code)
    },
    isDirty() {
      return JSON.stringify(this.form) !== this.saved
    },
    note() {
      return this.event.isMarketing
        ? 'Рекламное: не уходит гостям, отказавшимся от рассылок, и в тихие часы'
        : 'Уходит всегда: это не реклама'
    },
  },
  watch: {
    event() {
      this.reset()
    },
  },
  created() {
    this.reset()
  },
  methods: {
    ...mapActions('notificationSettings', ['updateEvent']),
    hasNotPermission(value) {
      return !hasPermission(value)
    },
    reset() {
      this.form = draftOf(this.event)
      this.saved = JSON.stringify(this.form)
      this.cursor = null
    },
    has(parameter) {
      return this.event.parameters.includes(parameter)
    },
    messages(property) {
      return errorMessages(this.errorsOf(this.key), property)
    },
    placeholder(name) {
      return `{${name}}`
    },
    isDefaultText(text) {
      return text.title === (text.defaultTitle || '') && text.body === (text.defaultBody || '')
    },

    restore(text) {
      text.title = text.defaultTitle || ''
      text.body = text.defaultBody || ''
    },

    remember(text, field, { target }) {
      this.cursor = { text, field, input: target }
    },
    insert(name) {
      if (!this.cursor) return

      const { text, field, input } = this.cursor
      const value = text[field]
      const position = input.selectionStart + this.placeholder(name).length

      text[field] = value.slice(0, input.selectionStart) + this.placeholder(name) + value.slice(input.selectionEnd)

      this.$nextTick(() => {
        input.focus()
        input.setSelectionRange(position, position)
      })
    },
    async save() {
      if (!this.$refs.form.validate()) return

      const event = this.event
      const sent = JSON.stringify(this.form)
      const isSaved = await this.updateEvent({ code: event.code, event: this.form })

      if (isSaved && this.event === event) {
        this.saved = sent
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.event-setting__state {
  min-width: 86px;
  justify-content: center;
}
</style>
